import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';

import { deriveInspection, useInspection } from '../inspect';
import { useLiveWire } from '../wire';
import { useWorldModel } from '../world';
import { useContextMenu } from './store';
import type { MenuItem } from './store';
import styles from './context-menu.module.css';

export function ContextMenu(): JSX.Element | null {
  const open = useContextMenu((s) => s.open);
  const screenX = useContextMenu((s) => s.screenX);
  const screenY = useContextMenu((s) => s.screenY);
  const items = useContextMenu((s) => s.items);
  const close = useContextMenu((s) => s.close);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<React.CSSProperties>({
    left: 0,
    top: 0,
  });

  const handleAction = useCallback(
    (item: MenuItem) => {
      if (item.kind === 'inspect') {
        const model = useWorldModel.getState().model;
        const data = deriveInspection(model, item.target);
        if (data) {
          useInspection.getState().openWith(item.target, data);
        }
      } else if (item.kind === 'walk') {
        if ('Tile' in item.target) {
          const [tileX, tileY] = item.target.Tile;
          useLiveWire.getState().send({
            plane: 'World',
            intent: { MoveTo: { target: [tileX, tileY] } },
          });
        }
      } else if (item.kind === 'fish') {
        if ('Tile' in item.target) {
          const [tileX, tileY] = item.target.Tile;
          useLiveWire.getState().send({
            plane: 'World',
            intent: { Fish: { target: [tileX, tileY] } },
          });
        }
      }
      close();
    },
    [close],
  );

  // Measure after DOM mount, adjust before paint — no visible flash.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const style: React.CSSProperties = { left: screenX, top: screenY };
    if (screenX + rect.width > window.innerWidth) {
      style.left = screenX - rect.width;
    }
    if (screenY + rect.height > window.innerHeight) {
      style.top = screenY - rect.height;
    }
    setPosition(style);
  }, [open, screenX, screenY]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    const onMouseDown = (e: MouseEvent): void => {
      const node = menuRef.current;
      if (node && !node.contains(e.target as Node)) {
        close();
      }
    };
    const onScroll = (): void => {
      close();
    };

    window.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => {
      window.addEventListener('mousedown', onMouseDown);
    }, 0);
    window.addEventListener('wheel', onScroll, { passive: true });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('wheel', onScroll);
    };
  }, [open, close]);

  if (!open) return null;

  const inspectItems = items.filter((i) => i.kind === 'inspect');
  const walkItem = items.find((i) => i.kind === 'walk');
  const fishItem = items.find((i) => i.kind === 'fish');

  return (
    <div className={styles.overlay} style={position} ref={menuRef}>
      <div className={styles.menu}>
        {inspectItems.map((item, i) => (
          <button
            key={i}
            className={styles.row}
            onClick={() => handleAction(item)}
          >
            <span className={`${styles.icon} ${styles[`icon_${item.entityType}`]}`} />
            <span className={styles.label}>
              Inspect <span className={styles.entityName}>{item.label}</span>
            </span>
          </button>
        ))}
        {walkItem && (
          <>
            <hr className={styles.separator} />
            <button
              className={styles.row}
              onClick={() => handleAction(walkItem)}
            >
              <span className={styles.walkIcon}>➔</span>
              <span className={styles.label}>Walk Here</span>
            </button>
          </>
        )}
        {fishItem && (
          <>
            <hr className={styles.separator} />
            <button
              className={styles.row}
              onClick={() => handleAction(fishItem)}
            >
              <span className={styles.walkIcon}>{'≈'}</span>
              <span className={styles.label}>Fish</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
